import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, forkJoin, map, of, Subscription } from 'rxjs';
import { Customer, PhoneService, UserAccount } from './session.model';
import { environment } from '../../../environments/environment';
import { extractErrorMessage } from '../../shared/utils/extract-error-message';
interface Brand {
  id: number;
  name: string;
}

@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly http = inject(HttpClient);
  private loadSubscription: Subscription | null = null;

  readonly customer = signal<Customer | null>(null);
  readonly phoneServices = signal<PhoneService[]>([]);
  readonly userAccounts = signal<UserAccount[]>([]);
  readonly loading = signal<boolean>(false);
  readonly error = signal<string | null>(null);

  readonly selectedUserAccount = signal<UserAccount | null>(null);
  readonly selectedPhoneService = signal<PhoneService | null>(null);

  readonly phoneServicesForSelectedUA: Signal<PhoneService[]> = computed(() => {
    const ua = this.selectedUserAccount();
    if (!ua) return [];
    return this.phoneServices().filter(ps => ps.userAccountId === ua.id);
  });

  /** Brand lookup: id → name (loaded once from API, cached) */
  private readonly brandMap = signal<Map<number, string>>(new Map());
  private brandsLoaded = false;

  /** All brand names sorted alphabetically — available for search filters */
  readonly brandNames: Signal<string[]> = computed(() => {
    const names = [...this.brandMap().values()];
    return names.sort((a, b) => a.localeCompare(b));
  });

  readonly customerId = computed(() => this.customer()?.id ?? null);
  readonly brandId = computed(() => this.customer()?.brandId ?? null);
  /**
   * Numeric language id of the selected customer (`mainCustomerLanguage`) — the
   * language every customer-facing localization lookup has to run in. Old CMT
   * passes `customer.mainCustomerLanguage` into the i18n calls
   * (`contracts.dir.js:65-66`); AMIE call sites used to hardcode `1` (EB-4143).
   */
  readonly customerLanguage = computed(() => this.customer()?.mainCustomerLanguage ?? null);
  readonly brandName = computed(() => {
    const id = this.brandId();
    if (!id) return '';
    return this.brandMap().get(id) ?? `Brand ${id}`;
  });
  readonly isLoaded = computed(() => this.customer() !== null && !this.loading());

  loadCustomer(customerId: number): void {
    this.loadSubscription?.unsubscribe();
    this.loading.set(true);
    this.error.set(null);

    // Load brands once (cached)
    this.loadBrands();

    this.loadSubscription = forkJoin({
      customer: this.http.get<Customer>(`${environment.contractUrl}/customers/${customerId}`).pipe(
        map(c => ({ ...c, id: Number(c.id), brandId: Number(c.brandId) })),
      ),
      phoneServices: this.http.get<PhoneService[]>(`${environment.phoneServiceUrl}/phoneservices`, {
        params: { customerId: customerId.toString() },
      }).pipe(
        map(ps => ps.map(p => ({ ...p, id: Number(p.id), customerId: Number(p.customerId) }))),
        catchError(() => of([] as PhoneService[])),
      ),
      userAccounts: this.http.get<UserAccount[]>(`${environment.userAccountUrl}/useraccounts`, {
        params: { customerId: customerId.toString() },
      }).pipe(
        map(uas => uas.map(ua => ({ ...ua, id: Number(ua.id), customerId: Number(ua.customerId) }))),
        catchError(() => of([] as UserAccount[])),
      ),
    }).subscribe({
      next: (result) => {
        this.customer.set(result.customer);
        this.phoneServices.set(result.phoneServices);
        this.userAccounts.set(result.userAccounts);

        // Auto-select first UA (sorted by lastName) and its related PS
        const sortedUAs = [...result.userAccounts].sort((a, b) =>
          (a.lastName || '').localeCompare(b.lastName || ''));
        if (sortedUAs.length > 0) {
          this.selectUserAccount(sortedUAs[0]);
        } else {
          this.selectedUserAccount.set(null);
          this.selectedPhoneService.set(null);
        }

        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(extractErrorMessage(err, 'Failed to load customer data'));
        this.customer.set(null);
        this.phoneServices.set([]);
        this.userAccounts.set([]);
        this.loading.set(false);
      },
    });
  }

  selectUserAccount(ua: UserAccount): void {
    if (ua.customerId !== this.customerId()) return;
    this.selectedUserAccount.set(ua);
    // Auto-select first related PS
    const relatedPs = this.phoneServices().filter(ps => ps.userAccountId === ua.id);
    this.selectedPhoneService.set(relatedPs.length > 0 ? relatedPs[0] : null);
  }

  selectPhoneService(ps: PhoneService): void {
    if (ps.customerId !== this.customerId()) return;
    this.selectedPhoneService.set(ps);
  }

  selectUserAccountById(uaId: number): void {
    const ua = this.userAccounts().find(u => u.id === uaId);
    if (ua) this.selectUserAccount(ua);
  }

  selectPhoneServiceById(psId: number): void {
    const ps = this.phoneServices().find(p => p.id === psId);
    if (ps) {
      // Also select the UA that owns this PS
      const ua = this.userAccounts().find(u => u.id === ps.userAccountId);
      if (ua) this.selectedUserAccount.set(ua);
      this.selectedPhoneService.set(ps);
    }
  }

  reloadCustomer(): void {
    const id = this.customerId();
    if (id !== null) {
      this.loadCustomer(id);
    }
  }

  /** Ensure brands are loaded (safe to call multiple times, fetches only once) */
  ensureBrandsLoaded(): void {
    this.loadBrands();
  }

  private loadBrands(): void {
    if (this.brandsLoaded) return;
    this.brandsLoaded = true;
    this.http.get<Brand[]>(`${environment.contractUrl}/brands`).pipe(
      catchError(() => of([])),
    ).subscribe((brands) => {
      const m = new Map<number, string>();
      for (const b of brands) {
        m.set(b.id, b.name);
      }
      this.brandMap.set(m);
    });
  }

  clearSession(): void {
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = null;
    this.customer.set(null);
    this.phoneServices.set([]);
    this.userAccounts.set([]);
    this.selectedUserAccount.set(null);
    this.selectedPhoneService.set(null);
    this.loading.set(false);
    this.error.set(null);
  }
}
